import { useEffect, useRef, useState } from "react";
import { supabase } from "lib/supabase";
import * as XLSX from "xlsx";
import {
  Download,
  Upload,
  X,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Calendar,
  AlertTriangle,
} from "lucide-react";
import { createPersonnel } from "services/personnelService";

const TEMPLATE_URL = "/templates/MasterCSVUpload.xlsx";

// ─── Parse uploaded file ──────────────────────────────────────────────────────
// Sheet structure (from actual template):
//   Masters  : group, cost_min, cost_max, department, experience_level, discount_type, burden_type, burden_type Unit
//   Role     : Group, Role
//   Level    : Group, Level, Cost
//   Personnel: Name, Email, Phone, Group, Role, Level, Department, Experience_level, is_sme
//   Discount : Discount_type, Discount_name, Discount_percent
//   Burden   : Name, Desc, [burden_type columns...]

const parseUploadedFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const sheetToRows = (name) => {
          const ws = wb.Sheets[name];
          if (!ws) return [];
          return XLSX.utils.sheet_to_json(ws, { defval: "" });
        };
        resolve({
          masters: sheetToRows("Masters"),
          roles: sheetToRows("Role"),
          levels: sheetToRows("Level"),
          personnel: sheetToRows("Personnel"),
          discount: sheetToRows("Discount"),
          burden: sheetToRows("Burden"),
        });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isSmeRow = (row) =>
  row.is_sme === true ||
  row.is_sme === 1 ||
  String(row.is_sme || "").toLowerCase() === "true";

const discountDupKey = (discountId, discountType) =>
  `${discountId}::${String(discountType || "")
    .trim()
    .toLowerCase()}`;

// ─── Burden type unit normalization ───────────────────────────────────────────
const VALID_UNITS = new Set(["pct", "mult", "flat"]);
const UNIT_ALIASES = {
  pct: "pct",
  percent: "pct",
  percentage: "pct",
  "%": "pct",
  mult: "mult",
  multiplier: "mult",
  multiply: "mult",
  flat: "flat",
  fixed: "flat",
};
const normalizeUnit = (raw) => {
  const key = String(raw || "")
    .trim()
    .toLowerCase();
  if (UNIT_ALIASES[key]) return UNIT_ALIASES[key];
  if (VALID_UNITS.has(key)) return key;
  return "pct";
};

// ─── Conflict detection ───────────────────────────────────────────────────────
const detectConflicts = (rows, field, existingNames) => {
  const canonical = {};
  (existingNames || []).forEach((n) => {
    const raw = String(n ?? "");
    const norm = raw.trim().toLowerCase();
    if (norm && !(norm in canonical)) canonical[norm] = raw;
  });

  const conflicts = [];
  const seenVariants = new Set();

  rows.forEach((row) => {
    const raw = row[field];
    if (!raw) return;
    const rawStr = String(raw);
    const norm = rawStr.trim().toLowerCase();
    if (!norm) return;

    if (!(norm in canonical)) {
      canonical[norm] = rawStr;
      return;
    }

    const canonicalRaw = canonical[norm];
    if (rawStr !== canonicalRaw && !seenVariants.has(rawStr)) {
      seenVariants.add(rawStr);
      conflicts.push({
        id: `${field}::${norm}::${rawStr}`,
        type: field,
        canonicalName: canonicalRaw,
        variantName: rawStr,
      });
    }
  });

  return conflicts;
};

// ─── Role-mismatch detection ──────────────────────────────────────────────────
const detectRoleMismatches = (personnelRows, roleSheetRows) => {
  const groupRoleMap = {};
  roleSheetRows.forEach((r) => {
    if (!r.Group || !r.Role) return;
    const gKey = String(r.Group).trim().toLowerCase();
    const rKey = String(r.Role).trim().toLowerCase();
    if (!groupRoleMap[gKey]) groupRoleMap[gKey] = new Set();
    groupRoleMap[gKey].add(rKey);
  });

  const mismatches = [];
  personnelRows.forEach((row, idx) => {
    if (!row.Group || !row.Role) return;
    const gKey = String(row.Group).trim().toLowerCase();
    const rKey = String(row.Role).trim().toLowerCase();
    const validRoles = groupRoleMap[gKey];
    if (validRoles && !validRoles.has(rKey)) {
      mismatches.push({
        idx,
        name: row.Name || "—",
        email: row.Email || "—",
        originalGroup: row.Group,
        originalRole: row.Role,
        validRolesForGroup: roleSheetRows
          .filter((r) => String(r.Group).trim().toLowerCase() === gKey)
          .map((r) => String(r.Role).trim()),
        allGroups: [
          ...new Set(
            roleSheetRows.map((r) => String(r.Group).trim()).filter(Boolean),
          ),
        ],
      });
    }
  });

  return mismatches;
};

// ─── Level-mismatch detection ─────────────────────────────────────────────────
const detectLevelMismatches = (personnelRows, levelSheetRows) => {
  const groupLevelMap = {};
  levelSheetRows.forEach((r) => {
    if (!r.Group || (r.Level === undefined && r.Level !== 0)) return;
    const gKey = String(r.Group).trim().toLowerCase();
    const lKey = String(r.Level).trim().toLowerCase();
    if (!groupLevelMap[gKey]) groupLevelMap[gKey] = new Set();
    groupLevelMap[gKey].add(lKey);
  });

  const mismatches = [];
  personnelRows.forEach((row, idx) => {
    if (!row.Group || !row.Level) return;
    const gKey = String(row.Group).trim().toLowerCase();
    const lKey = String(row.Level).trim().toLowerCase();
    const validLevels = groupLevelMap[gKey];
    if (validLevels && !validLevels.has(lKey)) {
      mismatches.push({
        idx,
        name: row.Name || "—",
        email: row.Email || "—",
        originalGroup: row.Group,
        originalLevel: row.Level,
        validLevelsForGroup: levelSheetRows
          .filter((r) => String(r.Group).trim().toLowerCase() === gKey)
          .map((r) => String(r.Level).trim()),
      });
    }
  });

  return mismatches;
};

// ─── Resolution helpers ───────────────────────────────────────────────────────
const buildResolutionMap = (conflicts, resolutions) => {
  const map = {};
  conflicts.forEach((c) => {
    map[`${c.type}::${c.variantName}`] = resolutions[c.id] || "skip";
  });
  return map;
};

const masterKeyFor = (rawName, type, resolutionMap) => {
  if (!rawName) return null;
  const str = String(rawName);
  const resolution = resolutionMap[`${type}::${str}`];
  if (resolution === "create") return `__variant__:${type}:${str}`;
  return str.trim().toLowerCase();
};

const masterNameFor = (rawName, type, resolutionMap) => {
  const str = String(rawName);
  const resolution = resolutionMap[`${type}::${str}`];
  return resolution === "create" ? str : str.trim();
};

// ─── Preview table ────────────────────────────────────────────────────────────
const isPlaceholderColumn = (key) => /^_\d+$/.test(key.trim());

const PreviewTable = ({ title, rows, color = "blue" }) => {
  if (!rows?.length) return null;
  const headers = Object.keys(rows[0]).filter((h) => !isPlaceholderColumn(h));
  const colorMap = {
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    green: "bg-green-50 border-green-200 text-green-700",
    purple: "bg-purple-50 border-purple-200 text-purple-700",
    orange: "bg-orange-50 border-orange-200 text-orange-700",
    teal: "bg-teal-50 border-teal-200 text-teal-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
    cyan: "bg-cyan-50 border-cyan-200 text-cyan-700",
  };
  return (
    <div className="mb-4">
      <div
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border mb-2 ${colorMap[color]}`}
      >
        {title} — {rows.length} row{rows.length !== 1 ? "s" : ""}
      </div>
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-muted/50">
            <tr>
              {headers.map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-left font-semibold text-muted-foreground whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 5).map((row, i) => (
              <tr key={i} className="border-t">
                {headers.map((h) => (
                  <td
                    key={h}
                    className="px-3 py-2 text-foreground whitespace-nowrap"
                  >
                    {String(row[h] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length > 5 && (
              <tr className="border-t bg-muted/20">
                <td
                  colSpan={headers.length}
                  className="px-3 py-2 text-muted-foreground text-center"
                >
                  +{rows.length - 5} more rows not shown
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Role Mismatch Resolution Step ───────────────────────────────────────────
const RoleMismatchStep = ({
  mismatches,
  roleSheetRows,
  corrections,
  onCorrect,
}) => {
  const groupRoleMap = {};
  roleSheetRows.forEach((r) => {
    if (!r.Group || !r.Role) return;
    const gKey = String(r.Group).trim();
    if (!groupRoleMap[gKey]) groupRoleMap[gKey] = [];
    const role = String(r.Role).trim();
    if (!groupRoleMap[gKey].includes(role)) groupRoleMap[gKey].push(role);
  });

  const allGroups = Object.keys(groupRoleMap);

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle size={16} className="text-amber-500" />
        <p className="text-sm font-semibold text-foreground">
          Role / Group Mismatches
        </p>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        The following personnel have a role that doesn't belong to their
        selected group. Fix each one by choosing the correct group and role
        before importing.
      </p>

      <div className="space-y-3">
        {mismatches.map((m) => {
          const correction = corrections[m.idx] || {
            group: m.originalGroup,
            role: "",
          };
          const selectedGroup = correction.group || m.originalGroup;
          const rolesForGroup = groupRoleMap[selectedGroup] || [];

          return (
            <div
              key={m.idx}
              className="border border-amber-200 rounded-lg p-4 bg-amber-50"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-semibold text-amber-700">
                    {String(m.name)[0].toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground leading-tight">
                    {m.name}
                  </p>
                  <p className="text-xs text-muted-foreground">{m.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Group <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedGroup}
                    onChange={(e) => {
                      const newGroup = e.target.value;
                      onCorrect(m.idx, { group: newGroup, role: "" });
                    }}
                    className="w-full text-xs border border-border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    {allGroups.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={correction.role || ""}
                    onChange={(e) =>
                      onCorrect(m.idx, { ...correction, role: e.target.value })
                    }
                    className={`w-full text-xs border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                      !correction.role ? "border-red-300" : "border-border"
                    }`}
                  >
                    <option value="">Select role</option>
                    {rolesForGroup.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  {!correction.role && (
                    <p className="text-xs text-red-500 mt-0.5">Required</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── SME Date Collection Step ─────────────────────────────────────────────────
const SmeDateStep = ({ smeRows, smeDates, onDateChange }) => (
  <div>
    <div className="flex items-center gap-2 mb-1">
      <Calendar size={16} className="text-primary" />
      <p className="text-sm font-semibold text-foreground">
        SME Collaboration Dates
      </p>
    </div>
    <p className="text-xs text-muted-foreground mb-4">
      The following personnel are marked as SMEs. Provide their collaboration
      start and end dates before importing.
    </p>
    <div className="space-y-3">
      {smeRows.map((row, idx) => {
        const key = row.Email?.trim().toLowerCase() || String(idx);
        const dates = smeDates[key] || { start: "", end: "" };
        const startErr = dates.end && dates.start && dates.start > dates.end;
        return (
          <div key={key} className="border rounded-lg p-4 bg-muted/30">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-semibold text-primary">
                  {String(row.Name || "?")[0].toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground leading-tight">
                  {row.Name || "—"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {row.Email || "—"}
                </p>
              </div>
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200 font-medium">
                SME
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={dates.start}
                  onChange={(e) => onDateChange(key, "start", e.target.value)}
                  className={`w-full text-xs border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 ${!dates.start ? "border-red-300" : "border-border"}`}
                />
                {!dates.start && (
                  <p className="text-xs text-red-500 mt-0.5">Required</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  End Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={dates.end}
                  onChange={(e) => onDateChange(key, "end", e.target.value)}
                  min={dates.start || undefined}
                  className={`w-full text-xs border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 ${!dates.end || startErr ? "border-red-300" : "border-border"}`}
                />
                {!dates.end && (
                  <p className="text-xs text-red-500 mt-0.5">Required</p>
                )}
                {startErr && (
                  <p className="text-xs text-red-500 mt-0.5">
                    Must be after start date
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ─── Main modal ───────────────────────────────────────────────────────────────
// Steps:
//   0 = download template
//   1 = upload file
//   2 = review + name conflicts
//   3 = role-group mismatch resolution (only if mismatches exist)
//   4 = SME dates (only if SMEs exist)
//   done = result

const MasterUploadModal = ({
  open,
  onClose,
  onSuccess,
  orgId,
  currencyId,
  timelineId,
}) => {
  const [step, setStep] = useState(0);
  const [parsedData, setParsedData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [forceOpen, setForceOpen] = useState(false);

  const [levelMismatches, setLevelMismatches] = useState([]);
  const [existingGroups, setExistingGroups] = useState([]);
  const [existingRoles, setExistingRoles] = useState([]);
  const [existingLevels, setExistingLevels] = useState([]);
  const [existingBurdenTypes, setExistingBurdenTypes] = useState([]);
  // ── NEW: department & experience level ──
  const [existingDepartments, setExistingDepartments] = useState([]);
  const [existingExperienceLevels, setExistingExperienceLevels] = useState([]);

  const [conflicts, setConflicts] = useState([]);
  const [resolutions, setResolutions] = useState({});

  // Role-mismatch state
  const [mismatches, setMismatches] = useState([]);
  const [roleMismatchCorrections, setRoleMismatchCorrections] = useState({});

  // { [emailKey]: { start: "YYYY-MM-DD", end: "YYYY-MM-DD" } }
  const [smeDates, setSmeDates] = useState({});

  const fileRef = useRef();

  // Derive effective personnel list (mismatches patched with corrections)
  const effectivePersonnel = (parsedData?.personnel || []).map((row, idx) => {
    const correction = roleMismatchCorrections[idx];
    if (!correction) return row;
    return {
      ...row,
      Group: correction.group || row.Group,
      Role: correction.role || row.Role,
    };
  });

  const smeRows = effectivePersonnel.filter(isSmeRow);

  const allSmeDatesValid = smeRows.every((row) => {
    const key = row.Email?.trim().toLowerCase() || "";
    const dates = smeDates[key] || {};
    return dates.start && dates.end && dates.start <= dates.end;
  });

  const allMismatchesFixed = mismatches.every((m) => {
    const c = roleMismatchCorrections[m.idx];
    return c && c.group && c.role;
  });

  useEffect(() => {
    if (!open && !forceOpen) {
      setStep(0);
      setParsedData(null);
      setError(null);
      setResult(null);
      setConflicts([]);
      setResolutions({});
      setMismatches([]);
      setRoleMismatchCorrections({});
      setExistingGroups([]);
      setExistingRoles([]);
      setExistingLevels([]);
      setExistingBurdenTypes([]);
      // ── NEW ──
      setExistingDepartments([]);
      setExistingExperienceLevels([]);
      setSmeDates({});
      setLevelMismatches([]);
    }
  }, [open, forceOpen]);

  const handleClose = () => {
    setForceOpen(false);
    onClose?.();
  };

  const handleDateChange = (emailKey, field, value) => {
    setSmeDates((prev) => ({
      ...prev,
      [emailKey]: {
        ...(prev[emailKey] || { start: "", end: "" }),
        [field]: value,
      },
    }));
  };

  const handleCorrection = (idx, correction) => {
    setRoleMismatchCorrections((prev) => ({ ...prev, [idx]: correction }));
  };

  // ─── handleFileChange ────────────────────────────────────────────────────

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      const data = await parseUploadedFile(file);

      const hasMasters = data.masters.length > 0;
      const hasRoles = data.roles.length > 0;
      const hasLevels = data.levels.length > 0;
      const hasPersonnel = data.personnel.length > 0;

      if (!hasMasters && !hasRoles && !hasLevels && !hasPersonnel) {
        setError(
          "The file appears empty. Fill in at least one sheet before uploading.",
        );
        return;
      }

      const [
        { data: groupsData },
        { data: rolesData },
        { data: levelsData },
        { data: burdenTypesData },
        // ── NEW ──
        { data: departmentsData },
        { data: experienceLevelsData },
      ] = await Promise.all([
        supabase
          .from("md_groups")
          .select("id, name")
          .eq("organization_id", orgId)
          .eq("is_active", 0),
        supabase
          .from("md_roles")
          .select("id, name")
          .eq("organization_id", orgId)
          .eq("is_active", 0),
        supabase
          .from("md_levels")
          .select("id, name, group_id")
          .eq("organization_id", orgId)
          .eq("is_active", 0),
        supabase
          .from("md_burden_type")
          .select("id, name, unit")
          .eq("org_id", orgId)
          .eq("is_active", 0),
        // ── NEW: fetch existing departments & experience levels ──
        supabase
          .from("md_department")
          .select("id, name")
          .eq("organization_id", orgId)
          .eq("is_active", 0),
        supabase
          .from("md_experience_level")
          .select("id, name")
          .eq("organization_id", orgId)
          .eq("is_active", 0),
      ]);

      const groupConflicts = detectConflicts(
        data.masters,
        "group",
        (groupsData || []).map((g) => g.name),
      );
      const roleConflicts = detectConflicts(
        data.roles,
        "Role",
        (rolesData || []).map((r) => r.name),
      );
      // ── NEW: detect department & experience level name conflicts ──
      const deptConflicts = detectConflicts(
        data.masters,
        "department",
        (departmentsData || []).map((d) => d.name),
      );
      const expConflicts = detectConflicts(
        data.masters,
        "experience_level",
        (experienceLevelsData || []).map((x) => x.name),
      );

      const foundMismatches = detectRoleMismatches(data.personnel, data.roles);
      const foundLevelMismatches = detectLevelMismatches(
        data.personnel,
        data.levels,
      );

      setLevelMismatches(foundLevelMismatches);
      setExistingGroups(groupsData || []);
      setExistingRoles(rolesData || []);
      setExistingLevels(levelsData || []);
      setExistingBurdenTypes(burdenTypesData || []);
      // ── NEW ──
      setExistingDepartments(departmentsData || []);
      setExistingExperienceLevels(experienceLevelsData || []);

      setConflicts([
        ...groupConflicts,
        ...roleConflicts,
        ...deptConflicts,
        ...expConflicts,
      ]);
      setResolutions({});
      setMismatches(foundMismatches);
      setRoleMismatchCorrections({});
      setParsedData(data);

      // Pre-fill SME dates if sheet provides them
      const initialDates = {};
      data.personnel.filter(isSmeRow).forEach((row) => {
        const key = row.Email?.trim().toLowerCase() || "";
        if (!key) return;
        initialDates[key] = {
          start: row.collaborator_start_date || "",
          end: row.collaborator_end_date || "",
        };
      });
      setSmeDates(initialDates);

      setStep(2);
    } catch {
      setError(
        "Could not read the file. Make sure it's the downloaded template (.xlsx).",
      );
    }
    e.target.value = "";
  };

  // ─── Step navigation ──────────────────────────────────────────────────────
  const handleReviewNext = () => {
    if (mismatches.length > 0) {
      setStep(3);
    } else if (smeRows.length > 0) {
      setStep(4);
    } else {
      handleConfirm();
    }
  };

  const handleMismatchNext = () => {
    if (smeRows.length > 0) {
      setStep(4);
    } else {
      handleConfirm();
    }
  };

  // ─── insertGroups ─────────────────────────────────────────────────────────
  const insertGroups = async (
    masters,
    resolutionMap,
    existingGroupsList,
    user,
  ) => {
    const errors = [];
    let groupsCreated = 0,
      groupsSkipped = 0;

    const groupMap = Object.fromEntries(
      (existingGroupsList || []).map((g) => [
        String(g.name).trim().toLowerCase(),
        g.id,
      ]),
    );

    for (const row of masters) {
      if (!row.group) continue;

      const key = masterKeyFor(row.group, "group", resolutionMap);
      const name = masterNameFor(row.group, "group", resolutionMap);

      if (!groupMap[key]) {
        const costMin =
          row.cost_min !== "" &&
          row.cost_min !== undefined &&
          row.cost_min !== null
            ? Number(row.cost_min)
            : 1000;
        const costMax =
          row.cost_max !== "" &&
          row.cost_max !== undefined &&
          row.cost_max !== null
            ? Number(row.cost_max)
            : 1000;

        const { data: insertedGroup, error } = await supabase
          .from("md_groups")
          .insert({
            name,
            organization_id: orgId,
            cost_min: Number.isNaN(costMin) ? 1000 : costMin,
            cost_max: Number.isNaN(costMax) ? 1000 : costMax,
            currency_id: 5,
            timeline_id: timelineId,
            created_by: user?.id,
          })
          .select("id")
          .single();

        if (error) {
          errors.push(`Group "${row.group}": ${error.message}`);
        } else {
          groupMap[key] = insertedGroup.id;
          groupsCreated++;
        }
      } else {
        groupsSkipped++;
      }
    }

    return { groupMap, groupsCreated, groupsSkipped, errors };
  };

  // ─── insertRoles ──────────────────────────────────────────────────────────
  const insertRoles = async (
    roles,
    resolutionMap,
    groupMap,
    existingRolesList,
    user,
  ) => {
    const errors = [];
    let rolesCreated = 0,
      rolesSkipped = 0;

    const roleMap = Object.fromEntries(
      (existingRolesList || []).map((r) => [
        String(r.name).trim().toLowerCase(),
        r.id,
      ]),
    );

    for (const row of roles) {
      if (!row.Role) continue;

      const roleKey = masterKeyFor(row.Role, "Role", resolutionMap);
      const roleName = masterNameFor(row.Role, "Role", resolutionMap);
      const groupKey = row.Group
        ? masterKeyFor(row.Group, "group", resolutionMap)
        : null;
      const groupId = groupKey ? (groupMap[groupKey] ?? null) : null;

      if (!roleMap[roleKey]) {
        if (!groupId) {
          errors.push(
            `Role "${row.Role}": no matching group "${row.Group}" found or group failed to insert`,
          );
          continue;
        }

        const { data: insertedRole, error } = await supabase
          .from("md_roles")
          .insert({
            name: roleName,
            organization_id: orgId,
            group_id: groupId,
            created_by: user?.id,
          })
          .select("id")
          .single();

        if (error) {
          errors.push(`Role "${row.Role}": ${error.message}`);
        } else {
          roleMap[roleKey] = insertedRole.id;
          rolesCreated++;
        }
      } else {
        rolesSkipped++;
      }
    }

    return { roleMap, rolesCreated, rolesSkipped, errors };
  };

  // ─── insertLevels ─────────────────────────────────────────────────────────
  const insertLevels = async (
    levels,
    resolutionMap,
    groupMap,
    existingLevelsList,
    user,
  ) => {
    const errors = [];
    let levelsCreated = 0,
      levelsSkipped = 0;

    const levelMap = Object.fromEntries(
      (existingLevelsList || []).map((l) => [
        `${l.group_id}::${String(l.name).trim().toLowerCase()}`,
        l.id,
      ]),
    );

    for (const row of levels) {
      if (!row.Level && row.Level !== 0) continue;

      const levelName = String(row.Level).trim();
      if (!levelName) continue;

      const groupKey = row.Group
        ? masterKeyFor(row.Group, "group", resolutionMap)
        : null;
      const currentGroupId = groupKey ? (groupMap[groupKey] ?? null) : null;

      if (!currentGroupId) {
        errors.push(
          `Level "${levelName}": no matching group "${row.Group || ""}" found or group failed to insert, level skipped`,
        );
        continue;
      }

      const levelKey = `${currentGroupId}::${levelName.toLowerCase()}`;

      if (!levelMap[levelKey]) {
        const rawCost = row.Cost;
        const cost =
          rawCost !== "" && rawCost !== undefined && rawCost !== null
            ? Number(rawCost)
            : 1000;

        const { data: insertedLevel, error } = await supabase
          .from("md_levels")
          .insert({
            name: levelName,
            group_id: currentGroupId,
            costing: Number.isNaN(cost) ? 1000 : cost,
            organization_id: orgId,
            created_by: user?.id,
          })
          .select("id")
          .single();

        if (error) {
          errors.push(`Level "${levelName}": ${error.message}`);
        } else {
          levelMap[levelKey] = insertedLevel.id;
          levelsCreated++;
        }
      } else {
        levelsSkipped++;
      }
    }

    return { levelMap, levelsCreated, levelsSkipped, errors };
  };

  // ─── insertDepartments ────────────────────────────────────────────────────
  // Sourced from the "department" column of the Masters sheet.
  // Each unique department name is upserted into md_department.
  // Returns departmentMap: { nameLower → id }
  const insertDepartments = async (
    masters,
    resolutionMap,
    existingDepartmentsList,
    user,
  ) => {
    const errors = [];
    let departmentsCreated = 0,
      departmentsSkipped = 0;

    const departmentMap = Object.fromEntries(
      (existingDepartmentsList || []).map((d) => [
        String(d.name).trim().toLowerCase(),
        d.id,
      ]),
    );

    for (const row of masters) {
      if (!row.department) continue;

      const key = masterKeyFor(row.department, "department", resolutionMap);
      const name = masterNameFor(row.department, "department", resolutionMap);

      if (!departmentMap[key]) {
        const { data: insertedDept, error } = await supabase
          .from("md_department")
          .insert({ name, organization_id: orgId, created_by: user?.id })
          .select("id")
          .single();

        if (error) {
          errors.push(`Department "${row.department}": ${error.message}`);
        } else {
          departmentMap[key] = insertedDept.id;
          departmentsCreated++;
        }
      } else {
        departmentsSkipped++;
      }
    }

    return { departmentMap, departmentsCreated, departmentsSkipped, errors };
  };

  // ─── insertExperienceLevels ───────────────────────────────────────────────
  // Sourced from the "experience_level" column of the Masters sheet.
  // Each unique experience level name is upserted into md_experience_level.
  // Returns experienceLevelMap: { nameLower → id }
  const insertExperienceLevels = async (
    masters,
    resolutionMap,
    existingExperienceLevelsList,
    user,
  ) => {
    const errors = [];
    let experienceLevelsCreated = 0,
      experienceLevelsSkipped = 0;

    const experienceLevelMap = Object.fromEntries(
      (existingExperienceLevelsList || []).map((x) => [
        String(x.name).trim().toLowerCase(),
        x.id,
      ]),
    );

    for (const row of masters) {
      if (!row.experience_level) continue;

      const key = masterKeyFor(
        row.experience_level,
        "experience_level",
        resolutionMap,
      );
      const name = masterNameFor(
        row.experience_level,
        "experience_level",
        resolutionMap,
      );

      if (!experienceLevelMap[key]) {
        const { data: insertedExpLevel, error } = await supabase
          .from("md_experience_level")
          .insert({ name, organization_id: orgId, created_by: user?.id })
          .select("id")
          .single();

        if (error) {
          errors.push(
            `Experience level "${row.experience_level}": ${error.message}`,
          );
        } else {
          experienceLevelMap[key] = insertedExpLevel.id;
          experienceLevelsCreated++;
        }
      } else {
        experienceLevelsSkipped++;
      }
    }

    return {
      experienceLevelMap,
      experienceLevelsCreated,
      experienceLevelsSkipped,
      errors,
    };
  };

  // ─── insertBurdenTypes ────────────────────────────────────────────────────
  const insertBurdenTypes = async (masters, existingBurdenTypesList, user) => {
    const errors = [];
    let burdenTypesCreated = 0,
      burdenTypesSkipped = 0;

    const burdenTypeMap = Object.fromEntries(
      (existingBurdenTypesList || []).map((t) => [
        String(t.name).trim().toLowerCase(),
        { id: t.id, unit: t.unit || "pct" },
      ]),
    );

    for (const row of masters) {
      if (!row.burden_type) continue;

      const rawName = String(row.burden_type).trim();
      if (!rawName) continue;

      const typeKey = rawName.toLowerCase();
      const unit = normalizeUnit(row["burden_type Unit"]);

      if (!burdenTypeMap[typeKey]) {
        const { data: insertedType, error } = await supabase
          .from("md_burden_type")
          .insert({ name: rawName, unit, description: null, org_id: orgId })
          .select("id")
          .single();

        if (error) {
          errors.push(`Burden type "${rawName}": ${error.message}`);
        } else {
          burdenTypeMap[typeKey] = { id: insertedType.id, unit };
          burdenTypesCreated++;
        }
      } else {
        burdenTypesSkipped++;
      }
    }

    return { burdenTypeMap, burdenTypesCreated, burdenTypesSkipped, errors };
  };

  // ─── insertPersonnel ──────────────────────────────────────────────────────
  // Now resolves department_id and experience_level_id from their respective maps
  // instead of storing raw strings.
  const insertPersonnel = async (
    personnel,
    groupMap,
    roleMap,
    levelMap,
    departmentMap, // ── NEW ──
    experienceLevelMap, // ── NEW ──
    resolutionMap,
  ) => {
    if (!personnel?.length)
      return {
        inserted: 0,
        skipped: 0,
        skippedMissing: 0,
        skippedDuplicate: 0,
        errors: [],
      };

    const errors = [];
    let inserted = 0,
      skippedMissing = 0,
      skippedDuplicate = 0;

    const { data: existingPersonnel } = await supabase
      .from("personnel")
      .select("email")
      .eq("is_active", 0);

    const existingEmails = new Set(
      (existingPersonnel || []).map((p) => p.email?.toLowerCase()),
    );

    for (const row of personnel) {
      if (!row.Name || !row.Email) {
        skippedMissing++;
        continue;
      }
      if (existingEmails.has(row.Email.trim().toLowerCase())) {
        skippedDuplicate++;
        continue;
      }

      const groupKey = row.Group
        ? masterKeyFor(row.Group, "group", resolutionMap)
        : null;
      const roleKey = row.Role
        ? masterKeyFor(row.Role, "Role", resolutionMap)
        : null;
      const groupId = groupKey ? (groupMap?.[groupKey] ?? null) : null;
      const roleId = roleKey ? (roleMap?.[roleKey] ?? null) : null;

      let levelId = null;
      if (row.Level && groupId) {
        const lKey = `${groupId}::${String(row.Level).trim().toLowerCase()}`;
        levelId = levelMap?.[lKey] ?? null;
      }

      // ── NEW: resolve department_id ──
      let departmentId = null;
      if (row.Department) {
        const dKey = masterKeyFor(row.Department, "department", resolutionMap);
        departmentId = departmentMap?.[dKey] ?? null;
      }

      // ── NEW: resolve experience_level_id ──
      let experienceLevelId = null;
      if (row.Experience_level) {
        const xKey = masterKeyFor(
          row.Experience_level,
          "experience_level",
          resolutionMap,
        );
        experienceLevelId = experienceLevelMap?.[xKey] ?? null;
      }

      const isSme = isSmeRow(row);
      const emailKey = row.Email.trim().toLowerCase();
      const collabDates = isSme ? smeDates[emailKey] || {} : {};

      const payload = {
        name: row.Name.trim(),
        email: row.Email.trim(),
        phone: String(row.Phone || ""),
        position: "",
        // ── CHANGED: use FK ids instead of raw strings ──
        department_id: departmentId,
        experience_level_id: experienceLevelId,
        group_id: groupId,
        role_id: roleId,
        level_id: levelId,
        skills: [],
        certifications: [],
        organization_id: orgId,
        user_category_id: isSme ? 2 : 1,
        collaborator_start_date: isSme ? collabDates.start || null : null,
        collaborator_end_date: isSme ? collabDates.end || null : null,
        avatar_url: "",
      };

      try {
        await createPersonnel(payload);
        inserted++;
        existingEmails.add(emailKey);
      } catch (err) {
        errors.push(`"${row.Name}": ${err.message}`);
      }
    }

    return {
      inserted,
      skipped: skippedMissing + skippedDuplicate,
      skippedMissing,
      skippedDuplicate,
      errors,
    };
  };

  // ─── insertDiscounts ──────────────────────────────────────────────────────
  const insertDiscounts = async (discountRows) => {
    if (!discountRows?.length)
      return {
        inserted: 0,
        profilesCreated: 0,
        skippedDuplicate: 0,
        skippedMissing: 0,
        errors: [],
      };

    const errors = [];
    let inserted = 0,
      profilesCreated = 0,
      skippedDuplicate = 0,
      skippedMissing = 0;

    const { data: profiles, error: profilesError } = await supabase
      .from("ms_discount_profile")
      .select("id, type")
      .eq("organization_id", orgId);

    if (profilesError) {
      errors.push(`Could not load discount profiles: ${profilesError.message}`);
      return {
        inserted,
        profilesCreated,
        skippedDuplicate,
        skippedMissing,
        errors,
      };
    }

    const profileNameMap = Object.fromEntries(
      (profiles || []).map((p) => [
        String(p.type ?? "")
          .trim()
          .toLowerCase(),
        p.id,
      ]),
    );

    const { data: existingHeads } = await supabase
      .from("ms_head_discount_profile")
      .select("discount_id, discount_type")
      .eq("organization_id", orgId)
      .eq("is_active", 0);

    const existingKeys = new Set(
      (existingHeads || []).map((h) =>
        discountDupKey(h.discount_id, h.discount_type),
      ),
    );

    for (const row of discountRows) {
      const discountName = String(row.Discount_name || "").trim();
      const discountType = String(row.Discount_type || "").trim();
      const discountPercent = Number(row.Discount_percent);

      if (!discountName || !discountType || Number.isNaN(discountPercent)) {
        skippedMissing++;
        continue;
      }

      const nameKey = discountName.toLowerCase();
      let discountId = profileNameMap[nameKey];

      if (!discountId) {
        const { data: newProfile, error: createError } = await supabase
          .from("ms_discount_profile")
          .insert({
            organization_id: orgId,
            type: discountType,
            description: null,
            is_active: 0,
          })
          .select("id")
          .single();

        if (createError) {
          errors.push(
            `Discount profile "${discountType}": ${createError.message}`,
          );
          continue;
        }

        discountId = newProfile.id;
        profileNameMap[nameKey] = discountId;
        profilesCreated++;
      }

      const dupKey = discountDupKey(discountId, discountType);
      if (existingKeys.has(dupKey)) {
        skippedDuplicate++;
        continue;
      }

      const { error } = await supabase.from("ms_head_discount_profile").insert({
        organization_id: orgId,
        discount_id: discountId,
        discount_type: discountName,
        discount_percent: discountPercent,
      });

      if (error) {
        errors.push(`Discount row "${discountType}": ${error.message}`);
      } else {
        inserted++;
        existingKeys.add(dupKey);
      }
    }

    return {
      inserted,
      profilesCreated,
      skippedDuplicate,
      skippedMissing,
      errors,
    };
  };

  // ─── insertBurdens ────────────────────────────────────────────────────────
  const RESERVED_BURDEN_COLUMNS = new Set(["name", "desc"]);

  const insertBurdens = async (burdenRows, burdenTypeMap) => {
    if (!burdenRows?.length)
      return {
        inserted: 0,
        burdensCreated: 0,
        skippedDuplicate: 0,
        skippedMissing: 0,
        skippedUnknownColumn: 0,
        errors: [],
      };

    const errors = [];
    let inserted = 0,
      burdensCreated = 0,
      skippedDuplicate = 0,
      skippedMissing = 0,
      skippedUnknownColumn = 0;

    const {
      data: { user },
    } = await supabase.auth.getUser();

    const { data: existingBurdens, error: burdensError } = await supabase
      .from("md_burdens")
      .select("id, name")
      .eq("org_id", orgId);

    if (burdensError) {
      errors.push(`Could not load burdens: ${burdensError.message}`);
      return {
        inserted,
        burdensCreated,
        skippedDuplicate,
        skippedMissing,
        skippedUnknownColumn,
        errors,
      };
    }

    const burdenNameMap = Object.fromEntries(
      (existingBurdens || []).map((b) => [
        String(b.name ?? "")
          .trim()
          .toLowerCase(),
        b.id,
      ]),
    );

    const burdenIds = (existingBurdens || []).map((b) => b.id);
    let existingKeys = new Set();
    if (burdenIds.length > 0) {
      const { data: existingMappings } = await supabase
        .from("md_burden_type_mapping")
        .select("burden_id, burden_type_id")
        .in("burden_id", burdenIds)
        .eq("is_active", 0);

      existingKeys = new Set(
        (existingMappings || []).map(
          (m) => `${m.burden_id}::${m.burden_type_id}`,
        ),
      );
    }

    for (const row of burdenRows) {
      const burdenName = String(row.Name || "").trim();
      const description = String(row.Desc || "").trim();

      if (!burdenName) {
        skippedMissing++;
        continue;
      }

      const burdenKey = burdenName.toLowerCase();
      let burdenId = burdenNameMap[burdenKey];

      if (!burdenId) {
        const { data: newBurden, error: createBurdenError } = await supabase
          .from("md_burdens")
          .insert({
            name: burdenName,
            description: description || null,
            org_id: orgId,
            created_by: user?.id,
          })
          .select("id")
          .single();

        if (createBurdenError) {
          errors.push(`Burden "${burdenName}": ${createBurdenError.message}`);
          continue;
        }

        burdenId = newBurden.id;
        burdenNameMap[burdenKey] = burdenId;
        burdensCreated++;
      }

      const mappingsToInsert = [];

      for (const [rawKey, rawValue] of Object.entries(row)) {
        const key = rawKey.trim();
        if (RESERVED_BURDEN_COLUMNS.has(key.toLowerCase())) continue;
        if (isPlaceholderColumn(key)) continue;
        if (rawValue === "" || rawValue === null || rawValue === undefined)
          continue;

        const value = Number(rawValue);
        if (Number.isNaN(value)) continue;

        const typeEntry = burdenTypeMap[key.toLowerCase()];
        if (!typeEntry) {
          skippedUnknownColumn++;
          errors.push(
            `Burden "${burdenName}": column "${key}" doesn't match any burden type, skipped`,
          );
          continue;
        }

        const dupKey = `${burdenId}::${typeEntry.id}`;
        if (existingKeys.has(dupKey)) {
          skippedDuplicate++;
          continue;
        }

        mappingsToInsert.push({
          burden_id: burdenId,
          burden_type_id: typeEntry.id,
          value,
          created_by: user?.id,
        });
        existingKeys.add(dupKey);
      }

      if (mappingsToInsert.length > 0) {
        const { error: mappingError } = await supabase
          .from("md_burden_type_mapping")
          .insert(mappingsToInsert);
        if (mappingError) {
          errors.push(`Burden "${burdenName}": ${mappingError.message}`);
        } else {
          inserted += mappingsToInsert.length;
        }
      }
    }

    return {
      inserted,
      burdensCreated,
      skippedDuplicate,
      skippedMissing,
      skippedUnknownColumn,
      errors,
    };
  };

  // ─── handleConfirm ────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    setUploading(true);
    setError(null);

    const resolutionMap = buildResolutionMap(conflicts, resolutions);

    let groupsResult = {
      groupMap: {},
      groupsCreated: 0,
      groupsSkipped: 0,
      errors: [],
    };
    let rolesResult = {
      roleMap: {},
      rolesCreated: 0,
      rolesSkipped: 0,
      errors: [],
    };
    let levelsResult = {
      levelMap: {},
      levelsCreated: 0,
      levelsSkipped: 0,
      errors: [],
    };
    let burdenTypesResult = {
      burdenTypeMap: {},
      burdenTypesCreated: 0,
      burdenTypesSkipped: 0,
      errors: [],
    };
    // ── NEW ──
    let departmentsResult = {
      departmentMap: {},
      departmentsCreated: 0,
      departmentsSkipped: 0,
      errors: [],
    };
    let experienceLevelsResult = {
      experienceLevelMap: {},
      experienceLevelsCreated: 0,
      experienceLevelsSkipped: 0,
      errors: [],
    };

    let personnelResult = {
      inserted: 0,
      skipped: 0,
      skippedMissing: 0,
      skippedDuplicate: 0,
      errors: [],
    };
    let discountResult = {
      inserted: 0,
      profilesCreated: 0,
      skippedDuplicate: 0,
      skippedMissing: 0,
      errors: [],
    };
    let burdenResult = {
      inserted: 0,
      burdensCreated: 0,
      skippedDuplicate: 0,
      skippedMissing: 0,
      skippedUnknownColumn: 0,
      errors: [],
    };

    const {
      data: { user },
    } = await supabase.auth.getUser();

    try {
      groupsResult = await insertGroups(
        parsedData.masters,
        resolutionMap,
        existingGroups,
        user,
      );
    } catch (err) {
      groupsResult.errors.push(
        `Groups import stopped early: ${err.message || "Unknown error"}`,
      );
    }

    try {
      rolesResult = await insertRoles(
        parsedData.roles,
        resolutionMap,
        groupsResult.groupMap,
        existingRoles,
        user,
      );
    } catch (err) {
      rolesResult.errors.push(
        `Roles import stopped early: ${err.message || "Unknown error"}`,
      );
    }

    try {
      levelsResult = await insertLevels(
        parsedData.levels,
        resolutionMap,
        groupsResult.groupMap,
        existingLevels,
        user,
      );
    } catch (err) {
      levelsResult.errors.push(
        `Levels import stopped early: ${err.message || "Unknown error"}`,
      );
    }

    try {
      burdenTypesResult = await insertBurdenTypes(
        parsedData.masters,
        existingBurdenTypes,
        user,
      );
    } catch (err) {
      burdenTypesResult.errors.push(
        `Burden types import stopped early: ${err.message || "Unknown error"}`,
      );
    }

    // ── NEW: insert departments & experience levels before personnel ──
    try {
      departmentsResult = await insertDepartments(
        parsedData.masters,
        resolutionMap,
        existingDepartments,
        user,
      );
    } catch (err) {
      departmentsResult.errors.push(
        `Departments import stopped early: ${err.message || "Unknown error"}`,
      );
    }

    try {
      experienceLevelsResult = await insertExperienceLevels(
        parsedData.masters,
        resolutionMap,
        existingExperienceLevels,
        user,
      );
    } catch (err) {
      experienceLevelsResult.errors.push(
        `Experience levels import stopped early: ${err.message || "Unknown error"}`,
      );
    }

    // Use effectivePersonnel — corrections already merged in.
    // Pass departmentMap and experienceLevelMap so personnel rows get FK ids.
    try {
      personnelResult = await insertPersonnel(
        effectivePersonnel,
        groupsResult.groupMap,
        rolesResult.roleMap,
        levelsResult.levelMap,
        departmentsResult.departmentMap, // ── NEW ──
        experienceLevelsResult.experienceLevelMap, // ── NEW ──
        resolutionMap,
      );
    } catch (err) {
      personnelResult.errors.push(
        `Personnel import stopped early: ${err.message || "Unknown error"}`,
      );
    }

    try {
      discountResult = await insertDiscounts(parsedData.discount);
    } catch (err) {
      discountResult.errors.push(
        `Discount import stopped early: ${err.message || "Unknown error"}`,
      );
    }

    try {
      burdenResult = await insertBurdens(
        parsedData.burden,
        burdenTypesResult.burdenTypeMap,
      );
    } catch (err) {
      burdenResult.errors.push(
        `Burden import stopped early: ${err.message || "Unknown error"}`,
      );
    }

    const finalResult = {
      groupsResult,
      rolesResult,
      levelsResult,
      burdenTypesResult,
      departmentsResult, // ── NEW ──
      experienceLevelsResult, // ── NEW ──
      personnelResult,
      discountResult,
      burdenResult,
    };
    // AFTER — only mark true if no errors occurred across all imports
    const hasAnyErrors =
      [
        ...groupsResult.errors,
        ...rolesResult.errors,
        ...levelsResult.errors,
        ...burdenTypesResult.errors,
        ...departmentsResult.errors,
        ...experienceLevelsResult.errors,
        ...personnelResult.errors,
        ...discountResult.errors,
        ...burdenResult.errors,
      ].length > 0;

    if (!hasAnyErrors) {
      const { error: flagError } = await supabase
        .from("user_org_memberships")
        .update({ first_bulk_upload: true })
        .eq("user_id", user.id)
        .eq("organization_id", orgId);

      if (flagError) {
        console.error("Failed to mark first_bulk_upload:", flagError);
      }
    }

    setResult(finalResult);
    setForceOpen(true);
    onSuccess?.(finalResult);
    setUploading(false);
  };

  if (!open && !forceOpen) return null;

  const allConflictsResolved = conflicts.every((c) => resolutions[c.id]);

  const allErrors = result
    ? [
        ...result.groupsResult.errors,
        ...result.rolesResult.errors,
        ...result.levelsResult.errors,
        ...result.burdenTypesResult.errors,
        // ── NEW ──
        ...result.departmentsResult.errors,
        ...result.experienceLevelsResult.errors,
        ...result.personnelResult.errors,
        ...result.discountResult.errors,
        ...result.burdenResult.errors,
      ]
    : [];

  const stepLabel = () => {
    if (result) return "Done";
    if (step === 2) return "Review";
    if (step === 3) return "Fix Role Mismatches";
    if (step === 4) return "SME Dates";
    return "";
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Masters & Personnel Upload
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {stepLabel() ? (
                <span className="text-primary font-medium">{stepLabel()}</span>
              ) : (
                "Download the template, fill it in, then upload to import all data together"
              )}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-md hover:bg-muted transition"
          >
            <X size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* ── Steps 0 & 1: Download / Upload ── */}
          {step < 2 && !result && (
            <div className="space-y-4">
              <div
                className={`border rounded-lg p-4 transition ${step === 0 ? "border-primary bg-primary/5" : "border-border bg-muted/20"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">
                      Step 1 — Download Template
                    </p>
                    <p className="text-xs text-muted-foreground">
                      The template has 6 sheets: <strong>Masters</strong>{" "}
                      (groups, departments, experience levels &amp; burden
                      types), <strong>Role</strong>, <strong>Level</strong>,{" "}
                      <strong>Personnel</strong>, <strong>Discount</strong>, and{" "}
                      <strong>Burden</strong>.
                    </p>
                  </div>
                  <a
                    href={TEMPLATE_URL}
                    download="RFP-Masters-Personnel-Template.xlsx"
                    onClick={() => setStep(1)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition flex-shrink-0"
                  >
                    <Download size={15} /> Download
                  </a>
                </div>
              </div>

              <div
                className={`border rounded-lg p-4 transition ${step === 1 ? "border-primary bg-primary/5" : "border-border"}`}
              >
                <p className="text-sm font-medium text-foreground mb-1">
                  Step 2 — Upload Filled Template
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  Upload the same template file after filling in your data.
                </p>
                <button
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-muted transition"
                >
                  <Upload size={15} /> Choose File
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
                  <AlertCircle
                    size={15}
                    className="text-red-500 mt-0.5 flex-shrink-0"
                  />
                  <p className="text-xs text-red-700">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* ── Step 2: Review ── */}
          {step === 2 && !result && (
            <div>
              <p className="text-sm font-medium text-foreground mb-3">
                Review data before importing
              </p>

              {/* Name conflicts */}
              {conflicts.length > 0 && (
                <div className="mb-4 border rounded-lg p-4 bg-yellow-50 border-yellow-200">
                  <p className="text-sm font-semibold text-yellow-800 mb-1">
                    Possible duplicate names found
                  </p>
                  <p className="text-xs text-yellow-700 mb-3">
                    These names look almost identical to an existing entry (case
                    or spacing differs). All must be resolved before importing.
                  </p>
                  <div className="space-y-2">
                    {conflicts.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center justify-between gap-3 bg-white border border-yellow-200 rounded-lg p-3"
                      >
                        <div className="text-xs">
                          <p className="text-foreground">
                            <span className="font-mono bg-muted px-1 rounded">
                              "{c.variantName}"
                            </span>{" "}
                            looks like{" "}
                            <span className="font-mono bg-muted px-1 rounded">
                              "{c.canonicalName}"
                            </span>
                          </p>
                          <p className="text-muted-foreground mt-0.5">
                            {c.type === "group"
                              ? "Group"
                              : c.type === "Role"
                                ? "Role"
                                : c.type === "department"
                                  ? "Department"
                                  : c.type === "experience_level"
                                    ? "Experience Level"
                                    : c.type}
                          </p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={() =>
                              setResolutions((r) => ({ ...r, [c.id]: "skip" }))
                            }
                            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition ${resolutions[c.id] === "skip" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
                          >
                            Skip (use existing)
                          </button>
                          <button
                            onClick={() =>
                              setResolutions((r) => ({
                                ...r,
                                [c.id]: "create",
                              }))
                            }
                            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition ${resolutions[c.id] === "create" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
                          >
                            Create new entry
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Role mismatch banner */}
              {mismatches.length > 0 && (
                <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <AlertTriangle
                    size={14}
                    className="text-amber-500 mt-0.5 flex-shrink-0"
                  />
                  <p className="text-xs text-amber-700">
                    <strong>
                      {mismatches.length} personnel row
                      {mismatches.length !== 1 ? "s have" : " has"} a role that
                      doesn't match their group.
                    </strong>{" "}
                    You'll fix them on the next step.
                  </p>
                </div>
              )}

              {/* Level mismatch banner */}
              {levelMismatches.length > 0 && (
                <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
                  <AlertCircle
                    size={14}
                    className="text-red-500 mt-0.5 flex-shrink-0"
                  />
                  <div className="text-xs text-red-700">
                    <strong>
                      {levelMismatches.length} personnel row
                      {levelMismatches.length !== 1 ? "s have" : " has"} a level
                      that doesn't belong to their group.
                    </strong>
                    <ul className="mt-1 space-y-0.5 list-disc list-inside">
                      {levelMismatches.map((m) => (
                        <li key={m.idx}>
                          <span className="font-medium">{m.name}</span> — level{" "}
                          <span className="font-mono bg-red-100 px-1 rounded">
                            "{m.originalLevel}"
                          </span>{" "}
                          is not valid for group{" "}
                          <span className="font-mono bg-red-100 px-1 rounded">
                            "{m.originalGroup}"
                          </span>
                          {m.validLevelsForGroup.length > 0 && (
                            <> (valid: {m.validLevelsForGroup.join(", ")})</>
                          )}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 font-medium">
                      Please fix these in your file and re-upload.
                    </p>
                  </div>
                </div>
              )}

              {/* SME notice */}
              {smeRows.length > 0 && (
                <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-purple-50 border border-purple-200">
                  <Calendar
                    size={14}
                    className="text-purple-500 mt-0.5 flex-shrink-0"
                  />
                  <p className="text-xs text-purple-700">
                    <strong>
                      {smeRows.length} SME{smeRows.length !== 1 ? "s" : ""}
                    </strong>{" "}
                    detected — collaboration dates required on a later step.
                  </p>
                </div>
              )}

              <PreviewTable
                title="Masters"
                rows={parsedData?.masters}
                color="blue"
              />
              <PreviewTable
                title="Role"
                rows={parsedData?.roles}
                color="teal"
              />
              <PreviewTable
                title="Level"
                rows={parsedData?.levels}
                color="rose"
              />
              <PreviewTable
                title="Personnel"
                rows={parsedData?.personnel}
                color="green"
              />
              <PreviewTable
                title="Discount"
                rows={parsedData?.discount}
                color="purple"
              />
              <PreviewTable
                title="Burden"
                rows={parsedData?.burden}
                color="orange"
              />

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 mt-3">
                  <AlertCircle
                    size={15}
                    className="text-red-500 mt-0.5 flex-shrink-0"
                  />
                  <p className="text-xs text-red-700">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Role-Group Mismatch Resolution ── */}
          {step === 3 && !result && (
            <RoleMismatchStep
              mismatches={mismatches}
              roleSheetRows={parsedData?.roles || []}
              corrections={roleMismatchCorrections}
              onCorrect={handleCorrection}
            />
          )}

          {/* ── Step 4: SME Dates ── */}
          {step === 4 && !result && (
            <SmeDateStep
              smeRows={smeRows}
              smeDates={smeDates}
              onDateChange={handleDateChange}
            />
          )}

          {/* ── Result ── */}
          {result && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 size={20} className="text-green-500" />
                <p className="text-sm font-semibold text-foreground">
                  Import Complete
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                  <p className="text-xs font-semibold text-blue-700 mb-2">
                    Groups
                  </p>
                  <p className="text-2xl font-bold text-blue-800">
                    {result.groupsResult.groupsCreated}
                  </p>
                  <p className="text-xs text-blue-600">created</p>
                  {result.groupsResult.groupsSkipped > 0 && (
                    <p className="text-xs text-blue-700 mt-1">
                      {result.groupsResult.groupsSkipped} already existed
                    </p>
                  )}
                  {result.groupsResult.errors.length > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      {result.groupsResult.errors.length} error(s)
                    </p>
                  )}
                </div>

                <div className="border rounded-lg p-4 bg-teal-50 border-teal-200">
                  <p className="text-xs font-semibold text-teal-700 mb-2">
                    Roles
                  </p>
                  <p className="text-2xl font-bold text-teal-800">
                    {result.rolesResult.rolesCreated}
                  </p>
                  <p className="text-xs text-teal-600">created</p>
                  {result.rolesResult.rolesSkipped > 0 && (
                    <p className="text-xs text-teal-700 mt-1">
                      {result.rolesResult.rolesSkipped} already existed
                    </p>
                  )}
                  {result.rolesResult.errors.length > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      {result.rolesResult.errors.length} error(s)
                    </p>
                  )}
                </div>

                <div className="border rounded-lg p-4 bg-rose-50 border-rose-200">
                  <p className="text-xs font-semibold text-rose-700 mb-2">
                    Levels
                  </p>
                  <p className="text-2xl font-bold text-rose-800">
                    {result.levelsResult.levelsCreated}
                  </p>
                  <p className="text-xs text-rose-600">created</p>
                  {result.levelsResult.levelsSkipped > 0 && (
                    <p className="text-xs text-rose-700 mt-1">
                      {result.levelsResult.levelsSkipped} already existed
                    </p>
                  )}
                  {result.levelsResult.errors.length > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      {result.levelsResult.errors.length} error(s)
                    </p>
                  )}
                </div>

                {/* ── NEW: Departments result card ── */}
                <div className="border rounded-lg p-4 bg-indigo-50 border-indigo-200">
                  <p className="text-xs font-semibold text-indigo-700 mb-2">
                    Departments
                  </p>
                  <p className="text-2xl font-bold text-indigo-800">
                    {result.departmentsResult.departmentsCreated}
                  </p>
                  <p className="text-xs text-indigo-600">created</p>
                  {result.departmentsResult.departmentsSkipped > 0 && (
                    <p className="text-xs text-indigo-700 mt-1">
                      {result.departmentsResult.departmentsSkipped} already
                      existed
                    </p>
                  )}
                  {result.departmentsResult.errors.length > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      {result.departmentsResult.errors.length} error(s)
                    </p>
                  )}
                </div>

                {/* ── NEW: Experience Levels result card ── */}
                <div className="border rounded-lg p-4 bg-cyan-50 border-cyan-200">
                  <p className="text-xs font-semibold text-cyan-700 mb-2">
                    Experience Levels
                  </p>
                  <p className="text-2xl font-bold text-cyan-800">
                    {result.experienceLevelsResult.experienceLevelsCreated}
                  </p>
                  <p className="text-xs text-cyan-600">created</p>
                  {result.experienceLevelsResult.experienceLevelsSkipped >
                    0 && (
                    <p className="text-xs text-cyan-700 mt-1">
                      {result.experienceLevelsResult.experienceLevelsSkipped}{" "}
                      already existed
                    </p>
                  )}
                  {result.experienceLevelsResult.errors.length > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      {result.experienceLevelsResult.errors.length} error(s)
                    </p>
                  )}
                </div>

                <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                  <p className="text-xs font-semibold text-green-700 mb-2">
                    Personnel
                  </p>
                  <p className="text-2xl font-bold text-green-800">
                    {result.personnelResult.inserted}
                  </p>
                  <p className="text-xs text-green-600">added</p>
                  <div className="text-xs text-yellow-600 mt-2 space-y-0.5">
                    {result.personnelResult.skippedDuplicate > 0 && (
                      <p>
                        {result.personnelResult.skippedDuplicate} skipped
                        (duplicate email)
                      </p>
                    )}
                    {result.personnelResult.skippedMissing > 0 && (
                      <p>
                        {result.personnelResult.skippedMissing} skipped (missing
                        name/email)
                      </p>
                    )}
                  </div>
                  {result.personnelResult.errors.length > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      {result.personnelResult.errors.length} error(s)
                    </p>
                  )}
                </div>

                <div className="border rounded-lg p-4 bg-purple-50 border-purple-200">
                  <p className="text-xs font-semibold text-purple-700 mb-2">
                    Discount
                  </p>
                  <p className="text-2xl font-bold text-purple-800">
                    {result.discountResult.inserted}
                  </p>
                  <p className="text-xs text-purple-600">
                    discount head(s) added
                  </p>
                  <div className="text-xs text-purple-700 mt-2 space-y-0.5">
                    {result.discountResult.profilesCreated > 0 && (
                      <p>
                        {result.discountResult.profilesCreated} new profile(s)
                        created
                      </p>
                    )}
                  </div>
                  <div className="text-xs text-yellow-600 mt-2 space-y-0.5">
                    {result.discountResult.skippedDuplicate > 0 && (
                      <p>
                        {result.discountResult.skippedDuplicate} skipped
                        (already exists)
                      </p>
                    )}
                    {result.discountResult.skippedMissing > 0 && (
                      <p>
                        {result.discountResult.skippedMissing} skipped (missing
                        required fields)
                      </p>
                    )}
                  </div>
                  {result.discountResult.errors.length > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      {result.discountResult.errors.length} error(s)
                    </p>
                  )}
                </div>

                <div className="border rounded-lg p-4 bg-orange-50 border-orange-200">
                  <p className="text-xs font-semibold text-orange-700 mb-2">
                    Burden
                  </p>
                  <p className="text-2xl font-bold text-orange-800">
                    {result.burdenResult.inserted}
                  </p>
                  <p className="text-xs text-orange-600">
                    value mapping(s) added
                  </p>
                  <div className="text-xs text-orange-700 mt-2 space-y-0.5">
                    {result.burdenTypesResult.burdenTypesCreated > 0 && (
                      <p>
                        {result.burdenTypesResult.burdenTypesCreated} new burden
                        type(s) created
                      </p>
                    )}
                    {result.burdenResult.burdensCreated > 0 && (
                      <p>
                        {result.burdenResult.burdensCreated} new burden(s)
                        created
                      </p>
                    )}
                  </div>
                  <div className="text-xs text-yellow-600 mt-2 space-y-0.5">
                    {result.burdenResult.skippedDuplicate > 0 && (
                      <p>
                        {result.burdenResult.skippedDuplicate} skipped (already
                        exists)
                      </p>
                    )}
                    {result.burdenResult.skippedMissing > 0 && (
                      <p>
                        {result.burdenResult.skippedMissing} skipped (missing
                        required fields)
                      </p>
                    )}
                  </div>
                  {result.burdenResult.errors.length > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      {result.burdenResult.errors.length} error(s)
                    </p>
                  )}
                </div>
              </div>

              {allErrors.length > 0 && (
                <div className="border rounded-lg p-3 bg-red-50 border-red-200 max-h-32 overflow-y-auto">
                  <p className="text-xs font-semibold text-red-700 mb-1">
                    Errors
                  </p>
                  {allErrors.map((e, i) => (
                    <p key={i} className="text-xs text-red-600">
                      {e}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted transition"
          >
            {result ? "Close" : "Cancel"}
          </button>

          {/* Review step footer */}
          {step === 2 && !result && (
            <>
              <button
                onClick={() => {
                  setStep(1);
                  setParsedData(null);
                  setError(null);
                  setConflicts([]);
                  setResolutions({});
                  setMismatches([]);
                  setRoleMismatchCorrections({});
                  setLevelMismatches([]);
                }}
                className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted transition"
              >
                Re-upload
              </button>
              <button
                onClick={handleReviewNext}
                disabled={!allConflictsResolved || levelMismatches.length > 0}
                className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition disabled:opacity-60"
              >
                {mismatches.length > 0 ? (
                  <>
                    <AlertTriangle size={15} /> Next: Fix Mismatches
                  </>
                ) : smeRows.length > 0 ? (
                  "Next: SME Dates"
                ) : (
                  <>
                    <CheckCircle2 size={15} /> Confirm & Import
                  </>
                )}
              </button>
            </>
          )}

          {/* Role mismatch step footer */}
          {step === 3 && !result && (
            <>
              <button
                onClick={() => setStep(2)}
                className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted transition"
              >
                Back
              </button>
              <button
                onClick={handleMismatchNext}
                disabled={!allMismatchesFixed}
                className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition disabled:opacity-60"
              >
                {smeRows.length > 0 ? (
                  "Next: SME Dates"
                ) : (
                  <>
                    <CheckCircle2 size={15} /> Confirm & Import
                  </>
                )}
              </button>
            </>
          )}

          {/* SME Dates step footer */}
          {step === 4 && !result && (
            <>
              <button
                onClick={() => setStep(mismatches.length > 0 ? 3 : 2)}
                className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted transition"
              >
                Back
              </button>
              <button
                onClick={handleConfirm}
                disabled={uploading || !allSmeDatesValid}
                className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition disabled:opacity-60"
              >
                {uploading ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <CheckCircle2 size={15} />
                )}
                {uploading ? "Importing…" : "Confirm & Import"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MasterUploadModal;
